// Module 02 — topology loader. Validates the committed data/reduced/*.json with the source Zod
// schemas, then normalizes the richer source vocabulary into the FROZEN domain enums and returns a
// ReductionInput-free ReducedGraph of domain-typed records. Pure & deterministic: same sources ->
// same graph. Throws (never silently loads) on malformed input. Imports only domain + zod + same
// module (CLAUDE.md boundary rule #2). The exact maps below are implementation behind reduction.v1.
import { z } from "zod";
import type {
  Coord,
  EdgeKind,
  Fuel,
  Generator,
  GridEdge,
  GridNode,
  Load,
  Polyline,
  VTier,
} from "../domain/index";
import { asBranchId, asEdgeId, asGenId, asNodeId } from "../domain/index";
import {
  SourceEdge,
  SourceGenerator,
  SourceLoad,
  SourceNode,
  SourceReport,
  type GeoAreaT,
} from "./schema";
import type { LoadReducedInput, ReductionReport, ReducedGraph } from "./types";

// ── source → domain normalization maps (free to change; not part of reduction.v1) ──────────────
// Fuel synonyms the source data uses → the 11 frozen domain Fuel members. Anything already a domain
// member falls through the `?? f` below.
const FUEL_MAP: Record<string, Fuel> = {
  BIOGAS_LFG: "BIOGAS",
  GAS_COGEN: "GAS_CHP",
  GAS_PEAKER: "GAS",
  GAS_OIL_STEAM: "GAS",
  BIOENERGY: "BIOGAS",
  WASTE_TO_ENERGY: "EFW",
};
const normalizeFuel = (f: string): Fuel => FUEL_MAP[f] ?? (f as Fuel);

// source edge kind "corridor" is a display-grouping synonym for a transmission line.
const normalizeEdgeKind = (k: string): EdgeKind => (k === "corridor" ? "line" : (k as EdgeKind));

// VTier preference for resolving an "UNK" junction from its incident edges: ties break toward the
// HIGHER voltage (a junction sitting where MV meets HV is treated as the more consequential tier).
const VTIER_ORDER: readonly VTier[] = ["EHV", "HV", "ST", "MV", "LV"];

// Resolve a node's vTier. A concrete source tier passes through; "UNK" is resolved to the modal
// tier among its incident edges (tie → higher voltage via VTIER_ORDER). Throws if an UNK junction
// has no incident edge to learn from — we never silently invent a tier.
function resolveVTier(srcTier: string, incident: ReadonlyMap<VTier, number>, nodeId: string): VTier {
  if (srcTier !== "UNK") return srcTier as VTier;
  let best: VTier | undefined;
  let bestCount = 0;
  for (const t of VTIER_ORDER) {
    const c = incident.get(t) ?? 0;
    if (c > bestCount) {
      bestCount = c;
      best = t;
    }
  }
  if (best === undefined) {
    throw new Error(`topology: UNK vTier node ${nodeId} has no incident edge tier to resolve from`);
  }
  return best;
}

// GeoJSON Polygon/MultiPolygon → the outer ring as a domain Polyline ([lon,lat] → {lat,lon}).
// Position arrays are guarded for noUncheckedIndexedAccess; a malformed (empty) ring throws.
function areaToPolyline(area: GeoAreaT, loadId: string): Polyline {
  const ring = area.type === "Polygon" ? area.coordinates[0] : area.coordinates[0]?.[0];
  if (ring === undefined) {
    throw new Error(`topology: load ${loadId} areaPolygon has no outer ring`);
  }
  return ring.map((pos): Coord => {
    const lon = pos[0];
    const lat = pos[1];
    if (lon === undefined || lat === undefined) {
      throw new Error(`topology: load ${loadId} areaPolygon has a malformed position`);
    }
    return { lat, lon };
  });
}

export function loadReduced(input: LoadReducedInput): ReducedGraph {
  const { sources } = input;

  const srcNodes = z.array(SourceNode).parse(sources.nodes);
  const srcEdges = z.array(SourceEdge).parse(sources.edges);
  const srcGens = z.array(SourceGenerator).parse(sources.generators);
  const srcLoads = z.array(SourceLoad).parse(sources.loads);
  const report = SourceReport.parse(sources.report);

  // Per-node incident-edge tier histogram, used only to resolve "UNK" node tiers.
  const incidentTiers = new Map<string, Map<VTier, number>>();
  const bump = (nodeId: string, tier: VTier): void => {
    let m = incidentTiers.get(nodeId);
    if (m === undefined) {
      m = new Map<VTier, number>();
      incidentTiers.set(nodeId, m);
    }
    m.set(tier, (m.get(tier) ?? 0) + 1);
  };
  for (const e of srcEdges) {
    bump(e.u, e.vTier);
    bump(e.v, e.vTier);
  }

  const emptyIncident = new Map<VTier, number>();

  const nodes: GridNode[] = srcNodes.map((n): GridNode => ({
    id: asNodeId(n.id),
    cls: n.cls,
    coord: n.coord,
    vTier: resolveVTier(n.vTier, incidentTiers.get(n.id) ?? emptyIncident, n.id),
    nominalKv: n.nominalKv,
    busType: n.busType,
    degree: n.degree,
    ...(n.name != null ? { name: n.name } : {}),
    ...(n.operator != null ? { operator: n.operator } : {}),
    ...(n.isTransformerStation !== undefined
      ? { isTransformerStation: n.isTransformerStation }
      : {}),
    ...(n.ownerRole != null ? { ownerRole: n.ownerRole } : {}),
    ...(n.ownerName != null ? { ownerName: n.ownerName } : {}),
    ...(n.transformerStationMva != null ? { transformerStationMva: n.transformerStationMva } : {}),
    ...(n.transformation != null ? { transformation: n.transformation } : {}),
    ...(n.transformerZPct != null ? { transformerZPct: n.transformerZPct } : {}),
    ...(n.displayOnly !== undefined ? { displayOnly: n.displayOnly } : {}),
    // V4 §3.5(c) — the facility's parcel-polygon OSM id (node↔parcel selection round-trip).
    ...(n.parcelId != null ? { parcelId: n.parcelId } : {}),
    // V3-swap (build log 75) — adopted superior V3 facility attrs (no-downgrade enrichment).
    ...(n.osmHub != null ? { osmHub: n.osmHub } : {}),
    ...(n.connectionKv != null ? { connectionKv: n.connectionKv } : {}),
    ...(n.stationMvaBasis != null ? { stationMvaBasis: n.stationMvaBasis } : {}),
    ...(n.attrSource != null ? { attrSource: n.attrSource } : {}),
    ...(n.attrConfidence != null ? { attrConfidence: n.attrConfidence } : {}),
  }));

  const edges: GridEdge[] = srcEdges.map((e): GridEdge => ({
    id: asEdgeId(e.id),
    u: asNodeId(e.u),
    v: asNodeId(e.v),
    kind: normalizeEdgeKind(e.kind),
    vTier: e.vTier,
    kv: e.kv,
    rPu: e.rPu,
    xPu: e.xPu,
    bPu: e.bPu,
    rateMva: e.rateMva,
    lengthKm: e.lengthKm,
    circuits: e.circuits,
    polyline: e.polyline,
    origBranchIds: e.origBranchIds.map(asBranchId),
    ...(e.isTie !== undefined ? { isTie: e.isTie } : {}),
    ...(e.isSwitchable !== undefined ? { isSwitchable: e.isSwitchable } : {}),
    ...(e.conductor != null ? { conductor: e.conductor } : {}),
    ...(e.thermalMva != null ? { thermalMva: e.thermalMva } : {}),
    ...(e.voltageInferred !== undefined ? { voltageInferred: e.voltageInferred } : {}),
    ...(e.displayOnly !== undefined ? { displayOnly: e.displayOnly } : {}),
    ...(e.isTransformerLeg !== undefined ? { isTransformerLeg: e.isTransformerLeg } : {}),
    ...(e.inferredBridge !== undefined ? { inferredBridge: e.inferredBridge } : {}),
    ...(e.serviceConnection !== undefined ? { serviceConnection: e.serviceConnection } : {}),
    ...(e.undergroundInferred !== undefined ? { undergroundInferred: e.undergroundInferred } : {}),
    ...(e.spurConnector !== undefined ? { spurConnector: e.spurConnector } : {}),
    ...(e.realCorridor !== undefined ? { realCorridor: e.realCorridor } : {}),
    ...(e.corridorRef != null ? { corridorRef: e.corridorRef } : {}),
    ...(e.corridorSource != null ? { corridorSource: e.corridorSource } : {}),
    ...(e.corridorConfidence != null ? { corridorConfidence: e.corridorConfidence } : {}),
    ...(e.corridorId != null ? { corridorId: asEdgeId(e.corridorId) } : {}),
    ...(e.circuitIndex != null ? { circuitIndex: e.circuitIndex } : {}),
    ...(e.maxXpuRatioSiblings != null ? { maxXpuRatioSiblings: e.maxXpuRatioSiblings } : {}),
    ...(e.coincident !== undefined ? { coincident: e.coincident } : {}),
    ...(e.normallyOpen !== undefined ? { normallyOpen: e.normallyOpen } : {}),
    // V3-swap (build log 75) — adopted superior V3 edge attrs (no-downgrade enrichment). `rewired` is a
    // pipeline-internal flag (validated-but-not-carried). uClosed/vClosed are DATA here (engine wiring later).
    ...(e.cables != null ? { cables: e.cables } : {}),
    ...(e.voltageProvenance != null ? { voltageProvenance: e.voltageProvenance } : {}),
    ...(e.uClosed !== undefined ? { uClosed: e.uClosed } : {}),
    ...(e.vClosed !== undefined ? { vClosed: e.vClosed } : {}),
    ...(e.attrSource != null ? { attrSource: e.attrSource } : {}),
    ...(e.supersededLegacy !== undefined ? { supersededLegacy: e.supersededLegacy } : {}),
  }));

  const generators: Generator[] = srcGens.map((g): Generator => ({
    id: asGenId(g.id),
    busId: asNodeId(g.busId),
    name: g.name,
    fuel: normalizeFuel(g.fuel),
    capMw: g.capMw,
    costPerMwh: g.costPerMwh,
    gridRole: g.gridRole,
    coord: g.coord,
    ...(g.mustRun !== undefined ? { mustRun: g.mustRun } : {}),
    // v3 restored dispatch-dynamics attributes — pass-through (domain.v3.md)
    ...(g.status !== undefined ? { status: g.status } : {}),
    ...(g.minOutputMw !== undefined ? { minOutputMw: g.minOutputMw } : {}),
    ...(g.rampRateMwPerMin !== undefined ? { rampRateMwPerMin: g.rampRateMwPerMin } : {}),
    ...(g.storageEnergyMwh !== undefined ? { storageEnergyMwh: g.storageEnergyMwh } : {}),
    ...(g.storageDurationH !== undefined ? { storageDurationH: g.storageDurationH } : {}),
    ...(g.chargeMw !== undefined ? { chargeMw: g.chargeMw } : {}), // P4 C34: explicit BATTERY C-rate ratings
    ...(g.dischargeMw !== undefined ? { dischargeMw: g.dischargeMw } : {}),
    ...(g.roundTripEfficiency !== undefined ? { roundTripEfficiency: g.roundTripEfficiency } : {}),
    ...(g.heatRateBtuPerKwh !== undefined ? { heatRateBtuPerKwh: g.heatRateBtuPerKwh } : {}),
    ...(g.co2KgPerMwh !== undefined ? { co2KgPerMwh: g.co2KgPerMwh } : {}),
    ...(g.capacityFactor !== undefined ? { capacityFactor: g.capacityFactor } : {}),
    ...(g.networkRole !== undefined ? { networkRole: g.networkRole } : {}),
    ...(g.meritRank !== undefined ? { meritRank: g.meritRank } : {}),
    ...(g.isChp !== undefined ? { isChp: g.isChp } : {}),
    ...(g.blackStartCapable !== undefined ? { blackStartCapable: g.blackStartCapable } : {}),
    ...(g.fuelSubtype !== undefined ? { fuelSubtype: g.fuelSubtype } : {}),
    ...(g.operator !== undefined ? { operator: g.operator } : {}),
    ...(g.iesoZone !== undefined ? { iesoZone: g.iesoZone } : {}),
    ...(g.availabilityFactor !== undefined ? { availabilityFactor: g.availabilityFactor } : {}),
    // P11 (domain.v4): delivered-cost pricing — drives the merit sort + λ when present on the live model.
    ...(g.distanceFromGtaKm !== undefined ? { distanceFromGtaKm: g.distanceFromGtaKm } : {}),
    ...(g.deliveredCostPerMwh !== undefined ? { deliveredCostPerMwh: g.deliveredCostPerMwh } : {}),
    ...(g.importCorridor !== undefined ? { importCorridor: g.importCorridor } : {}),
  }));

  const loads: Load[] = srcLoads.map((l): Load => ({
    id: asNodeId(l.id),
    name: l.name,
    kind: l.kind,
    busId: asNodeId(l.busId),
    maxDemandMw: l.maxDemandMw,
    criticalityPriority: l.criticalityPriority,
    developmentDensity: l.developmentDensity,
    coord: l.coord,
    ...(l.loopType != null ? { loopType: l.loopType } : {}),
    ...(l.areaPolygon !== undefined ? { areaPolygon: areaToPolyline(l.areaPolygon, l.id) } : {}),
    ...(l.peakSolarMw !== undefined ? { peakSolarMw: l.peakSolarMw } : {}),
    ...(l.batteryCapacityMwh !== undefined ? { batteryCapacityMwh: l.batteryCapacityMwh } : {}),
    ...(l.batteryChemistry !== undefined ? { batteryChemistry: l.batteryChemistry } : {}),
    ...(l.distLossCoef !== undefined ? { distLossCoef: l.distLossCoef } : {}), // F4a (P11): carry the stamped coef through
    // v3 restored per-neighbourhood demand model + prosumer economics — pass-through (domain.v3.md)
    ...(l.demandProfiles !== undefined ? { demandProfiles: l.demandProfiles } : {}),
    ...(l.peakHourSummer !== undefined ? { peakHourSummer: l.peakHourSummer } : {}),
    ...(l.peakHourWinter !== undefined ? { peakHourWinter: l.peakHourWinter } : {}),
    ...(l.seasonalScaling !== undefined ? { seasonalScaling: l.seasonalScaling } : {}),
    ...(l.weatherSensSummerMwPerDegC !== undefined
      ? { weatherSensSummerMwPerDegC: l.weatherSensSummerMwPerDegC }
      : {}),
    ...(l.weatherSensWinterMwPerDegC !== undefined
      ? { weatherSensWinterMwPerDegC: l.weatherSensWinterMwPerDegC }
      : {}),
    ...(l.annualConsumptionGwh !== undefined ? { annualConsumptionGwh: l.annualConsumptionGwh } : {}),
    ...(l.loadFactorPct !== undefined ? { loadFactorPct: l.loadFactorPct } : {}),
    ...(l.rooftopSuitabilityScore !== undefined
      ? { rooftopSuitabilityScore: l.rooftopSuitabilityScore }
      : {}),
    ...(l.batteryEtaAcAc !== undefined ? { batteryEtaAcAc: l.batteryEtaAcAc } : {}),
    ...(l.sellOfferPriceCkWh !== undefined ? { sellOfferPriceCkWh: l.sellOfferPriceCkWh } : {}),
    ...(l.population !== undefined ? { population: l.population } : {}),
    ...(l.populationDensityPerKm2 !== undefined
      ? { populationDensityPerKm2: l.populationDensityPerKm2 }
      : {}),
    ...(l.areaKm2 !== undefined ? { areaKm2: l.areaKm2 } : {}),
  }));

  const reductionReport: ReductionReport = {
    nodesBefore: report.nodesBefore,
    nodesAfter: report.nodesAfter,
    edgesBefore: report.edgesBefore,
    edgesAfter: report.edgesAfter,
    seriesEliminations: report.seriesEliminations,
    stubPrunes: report.stubPrunes,
    prunedBranches: report.prunedBranches,
    removedByKind: report.removedByKind,
    reducibleDeg2Left: report.reducibleDeg2Left,
    deg1StubsLeft: report.deg1StubsLeft,
  };

  return { nodes, edges, loads, generators, report: reductionReport };
}
